import { useState, useRef } from "react"
import { Upload, FileText, Image, X, CheckCircle2, Plus } from "lucide-react"

const ACCEPTED = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic"]
const MAX_SIZE_MB = 10

function FileCard({ file, onRemove }) {
  const isImage = file.type.startsWith("image/")
  const sizeLabel = file.size < 1024 * 1024
    ? `${(file.size / 1024).toFixed(0)} KB`
    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`

  return (
    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
      <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
        {isImage
          ? <Image className="w-4.5 h-4.5 text-indigo-600" />
          : <FileText className="w-4.5 h-4.5 text-indigo-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{file.name}</p>
        <p className="text-xs text-slate-400">{sizeLabel}</p>
      </div>
      <button
        onClick={onRemove}
        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

export function UploadResultsStep({ onNext }) {
  const [files, setFiles] = useState([])
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState("")
  const inputRef = useRef(null)

  const addFiles = (incoming) => {
    setError("")
    const valid = []
    for (const f of incoming) {
      if (!ACCEPTED.includes(f.type)) {
        setError("Only PDF and image files (JPG, PNG, WEBP, HEIC) are accepted.")
        continue
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`"${f.name}" is too large. Max size is ${MAX_SIZE_MB} MB.`)
        continue
      }
      // Avoid duplicates by name
      if (!files.some((existing) => existing.name === f.name)) {
        valid.push(f)
      }
    }
    setFiles((prev) => [...prev, ...valid])
  }

  const removeFile = (name) => setFiles((prev) => prev.filter((f) => f.name !== name))

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    addFiles(Array.from(e.dataTransfer.files))
  }

  const handleInput = (e) => {
    addFiles(Array.from(e.target.files))
    e.target.value = ""
  }

  const handleNext = () => {
    // Store file metadata — actual upload to backend happens post-onboarding
    const meta = files.map((f) => ({
      name: f.name,
      type: f.type,
      size: f.size,
      uploadedAt: new Date().toISOString(),
      status: "pending_upload",
    }))
    onNext({ resultFiles: meta })
  }

  return (
    <div className="space-y-5">
      <div className="text-center space-y-1">
        <h2 className="text-2xl font-black text-slate-900">Upload your results</h2>
        <p className="text-sm text-slate-500">
          Take a photo or upload your result slip. This helps us tailor your path — you can skip and add it later.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-2xl py-8 px-4 cursor-pointer transition-all
          ${dragOver
            ? "border-indigo-400 bg-indigo-50"
            : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED.join(",")}
          multiple
          className="hidden"
          onChange={handleInput}
        />

        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors
          ${dragOver ? "bg-indigo-100" : "bg-slate-100"}`}>
          <Upload className={`w-6 h-6 transition-colors ${dragOver ? "text-indigo-600" : "text-slate-400"}`} />
        </div>

        <div className="text-center">
          <p className="text-sm font-bold text-slate-700">
            {dragOver ? "Drop files here" : "Tap to upload or drag files here"}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">PDF, JPG, PNG, WEBP, HEIC · Max {MAX_SIZE_MB} MB each</p>
        </div>

        {files.length === 0 && (
          <div className="flex items-center gap-4 mt-1">
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <FileText className="w-3.5 h-3.5" />
              Result slip PDF
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <Image className="w-3.5 h-3.5" />
              Photo of results
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-600 font-medium bg-red-50 border border-red-100 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f) => (
            <FileCard key={f.name} file={f} onRemove={() => removeFile(f.name)} />
          ))}

          {/* Add more */}
          <button
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 py-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Add another result
          </button>
        </div>
      )}

      {/* Success note */}
      {files.length > 0 && (
        <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
          <p className="text-xs font-medium text-emerald-700">
            {files.length} file{files.length > 1 ? "s" : ""} ready — we&apos;ll process {files.length > 1 ? "them" : "it"} after you finish setup.
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2 pt-1">
        <button
          onClick={handleNext}
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-xl text-sm transition-all active:scale-95"
        >
          {files.length > 0 ? "Continue" : "Skip for now"}
        </button>
      </div>
    </div>
  )
}
